import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { aiToast } from './shared/ai-toast';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { AgentRunsTable } from './shared/agent-runs';
import UserSelect, { FilterUser } from './shared/user-select';

const AGENT_MODULES = [
  { id: 'attestation_review', label: 'Attestation review' },
  { id: 'recommendation_outreach', label: 'Recommendation outreach' },
  { id: 'budget_review', label: 'Budget review' },
] as const;

type AgentModuleId = (typeof AGENT_MODULES)[number]['id'];

function BotTestTab() {
  const [runningModule, setRunningModule] = useState<AgentModuleId | null>(
    null,
  );
  const [moduleUser, setModuleUser] = useState<
    Record<string, FilterUser | null>
  >({});
  const [moduleDeliver, setModuleDeliver] = useState<Record<string, boolean>>({});
  const [moduleDryRun, setModuleDryRun] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await fetchWrapper.get(urls.DevOps.agentSettings);
    } catch {
      aiToast.error('Failed to load agent settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const runModule = async (module: AgentModuleId) => {
    const user = moduleUser[module];
    if (!user) {
      aiToast.error('Select a user for this module run');
      return;
    }

    setRunningModule(module);
    try {
      const body: Record<string, unknown> = {
        module,
        user_id: user.id,
        deliver: moduleDeliver[module] === true,
        dry_run: moduleDryRun[module] === true,
      };
      await fetchWrapper.post(urls.DevOps.agentTriggerRun, body);
      aiToast.success('Agent run started');
    } catch (error: any) {
      aiToast.error(error?.message ?? 'Failed to start agent run');
    } finally {
      setRunningModule(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Agent — test runs</Typography>
        <Typography variant="body2" color="text.secondary">
          Trigger agent modules manually for a selected user. Scheduled
          configuration is managed under Bot (cron).
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Manual module runs
        </Typography>
        {AGENT_MODULES.map((module) => (
          <Grid
            container
            spacing={2}
            key={module.id}
            sx={{ mb: 2 }}
            alignItems="center">
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" fontWeight={600}>
                {module.label}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <UserSelect
                value={moduleUser[module.id] ?? null}
                onChange={(user) =>
                  setModuleUser((prev) => ({ ...prev, [module.id]: user }))
                }
                label="User"
                testId={`agent-run-user-${module.id}`}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={moduleDeliver[module.id] === true}
                    onChange={(e) =>
                      setModuleDeliver((prev) => ({
                        ...prev,
                        [module.id]: e.target.checked,
                      }))
                    }
                    data-testid={`agent-run-deliver-${module.id}`}
                  />
                }
                label="Deliver"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={moduleDryRun[module.id] === true}
                    onChange={(e) =>
                      setModuleDryRun((prev) => ({
                        ...prev,
                        [module.id]: e.target.checked,
                      }))
                    }
                    data-testid={`agent-run-dry-run-${module.id}`}
                  />
                }
                label="Dry run"
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => runModule(module.id)}
                disabled={runningModule !== null || !moduleUser[module.id]}
                data-testid={`agent-run-${module.id}`}
                sx={{ mt: 1, ml: 1 }}>
                {runningModule === module.id ? 'Starting…' : 'Run now'}
              </Button>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Grid item xs={12}>
        <AgentRunsTable
          triggerFilter="manual"
          title="Manual run history"
          testId="agent-manual-batch-runs"
        />
      </Grid>
    </Grid>
  );
}

export default BotTestTab;
